import React, { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { CircularProgress, Paper, Button } from '@material-ui/core/';
import Grid from '@material-ui/core/Grid';
import { showToast } from '@deeptrue-frontend/ui';

import Modal from 'components/Modal/Modal';
import { useFormatMessage } from 'components/IntlProvider';
import ROUTES from 'consts/routing';
import CreatorInstance from './CreatorInstance';
import api from 'api/api';

const useStyles = makeStyles({
  loader: {
    height: '100vh',
  },
  modal: {
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
  },
  btnBox: {
    marginTop: '2rem',
    display: 'flex',
    justifyContent: 'center',
    '& button': {
      margin: '0 0.5rem',
    },
  },
  btnRemove: {
    backgroundColor: '#b00020',

    '&:hover': {
      backgroundColor: '#b00020b0',
    },
  },
});

const TemplatesCreator = ({
  history,
  match,
}: RouteComponentProps & { match: { params: { id: number } } }) => {
  const [templateData, setTemplateData] = useState({
    templateId: match.params.id,
    isTemplateSaved: match.path === ROUTES.ADMIN.TEMPLATES.SURVEYS.EDIT,
    name: '',
    json: {},
    isLoadedFromLocal: false,
  });
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);

  const isLocalData = localStorage.getItem('surveyTemplate');

  const t = useFormatMessage();

  const classes = useStyles();
  const { templateId, isTemplateSaved, isLoadedFromLocal } = templateData;

  useEffect(() => {
    if (isTemplateSaved) {
      setIsLoadingData(true);
      api.resources.getTemplate(templateId).then(({ data }) => {
        setTemplateData({
          name: data.name,
          json: data.json,
          isTemplateSaved: true,
          templateId: data.id,
          isLoadedFromLocal: false,
        });
        if (isLocalData) {
          const { templateId } = JSON.parse(isLocalData);
          if (templateId === Number(match.params.id)) {
            setIsModalOpen(true);
          }
        }
        setIsLoadingData(false);
      });
    } else if (isLocalData) {
      setIsModalOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match.path]);
  // TO-DO cleanup values.values,
  const saveTemplateOnServer = ({
    values,
    withRedirect,
  }: {
    values: any;
    withRedirect?: boolean;
  }) => {
    if (!values.values || !values.values.name) return;
    const JSONTemplateString = localStorage.getItem('surveyTemplate');
    const JSONTemplate = JSONTemplateString
      ? JSON.parse(JSONTemplateString).json
      : undefined;

    const prepareData = {
      name: values.values.name,
      json: JSONTemplate,
    };

    if (isTemplateSaved)
      return api.resources
        .editTemplate(templateId, prepareData)
        .then(() => {
          if (withRedirect) {
            localStorage.removeItem('surveyTemplate');
            return history.push(
              `${ROUTES.ADMIN.TEMPLATES.SURVEYS.MAIN}?action=template_edited`
            );
          }
        })
        .catch(() => {
          showToast({
            type: 'error',
            title: t(
              'errors.toast.general',
              'Something went wrong, please contact administrator'
            ),
          });
        });
    return api.resources
      .createTemplate(prepareData)
      .then(({ data }) => {
        if (withRedirect) {
          localStorage.removeItem('surveyTemplate');
          return history.push(
            `${ROUTES.ADMIN.TEMPLATES.SURVEYS.MAIN}?action=template_created`
          );
        }
        setTemplateData({
          isTemplateSaved: true,
          templateId: data.id,
          name: data.name,
          json: data.json,
          isLoadedFromLocal: false,
        });
      })
      .catch(() => {
        showToast({
          type: 'error',
          title: t(
            'errors.toast.general',
            'Something went wrong, please contact administrator'
          ),
        });
      });
  };

  const saveTemplate = (values: any) => saveTemplateOnServer({ values });
  const saveAndExit = (values: any) =>
    saveTemplateOnServer({ values, withRedirect: true });

  const saveTemplateLocally = (data: any) => {
    if (isModalOpen) return;

    const { type, json, name, templateId } = data;
    const storageDataString = localStorage.getItem(`${type}`);
    if (storageDataString) {
      const storageData = JSON.parse(storageDataString);

      localStorage.setItem(
        `${type}`,
        JSON.stringify({
          templateId: templateId ? templateId : storageData.templateID,
          json: json ? json : storageData.json,
          name: name ? name : storageData.name,
        })
      );
    } else {
      localStorage.setItem(
        `${type}`,
        JSON.stringify({ templateId, json, name })
      );
    }
  };
  const initialValues =
    isTemplateSaved || isLoadedFromLocal ? templateData : null;

  return isLoadingData ? (
    <Grid
      className={classes.loader}
      container
      alignItems="center"
      justify="center"
      direction="column"
    >
      <CircularProgress size={80} color="primary" />
    </Grid>
  ) : (
    <>
      <Modal open={isModalOpen}>
        <Paper className={classes.modal}>
          {isDeleteConfirmationOpen
            ? t(
                'template.remove.confirm',
                'All unsaved data will be lost, Are you sure?'
              )
            : t(
                'template.unsaved',
                'You have unsaved work, would you like to restore it?'
              )}
          <div className={classes.btnBox}>
            {isDeleteConfirmationOpen ? (
              <>
                <Button
                  variant="contained"
                  color="secondary"
                  className={classes.btnRemove}
                  onClick={() => {
                    localStorage.removeItem('surveyTemplate');
                    setIsModalOpen(false);
                  }}
                >
                  {t('remove', 'remove')}
                </Button>
                <Button onClick={() => setIsDeleteConfirmationOpen(false)}>
                  {t('cancel', 'cancel')}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    const parsed = isLocalData && JSON.parse(isLocalData);

                    setTemplateData({
                      ...parsed,
                      isTemplateSaved: !!parsed.templateId,
                      isLoadedFromLocal: true,
                    });
                    setIsModalOpen(false);
                  }}
                >
                  {t('yes', 'yes')}
                </Button>
                <Button onClick={() => setIsDeleteConfirmationOpen(true)}>
                  {t('no', 'no')}
                </Button>
              </>
            )}
          </div>
        </Paper>
      </Modal>
      <CreatorInstance
        onSubmit={saveTemplateOnServer}
        initialValues={initialValues}
        backUrl={ROUTES.ADMIN.TEMPLATES.SURVEYS.MAIN}
        saveAndExit={saveAndExit}
        saveTemplate={saveTemplate}
        saveLocally={saveTemplateLocally}
        type="surveyTemplate"
      />
    </>
  );
};

export default TemplatesCreator;
